import * as d3 from "d3";
import { useEffect, useRef } from "react";
import useContainerDimensions from "./useContainerDimensions";

type DesnityProps = {
  data: number[];
  title: string;
  xLimMax: string;
  xLimMin: string;
};

const DensityPlot = ({ data, title, xLimMax, xLimMin }: DesnityProps) => {
  const ref = useRef<SVGSVGElement>(null);
  const {ref: containerRef, dimensions} = useContainerDimensions();
  const width = dimensions.width;
  const height = dimensions.height;

  useEffect(() => {

    if (!width || !height || !data || data.length === 0) {
        d3.select(ref.current).selectAll('*').remove();
        return;
    }

    const margin = { top: 30, right: 30, bottom: 30, left: 50 },
      width2 = width - margin.left - margin.right,
      height2 = height - margin.top - margin.bottom;

    // Clear previous chart
    d3.select(ref.current).selectAll("*").remove();
    // append the svg object to the body of the page
    const svg = d3
      .select(ref.current)
      .append("svg")
      .attr("width", width2 + margin.left + margin.right)
      .attr("height", height2 + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    //Limites proporcionados por el usuario
    const userXMin = parseFloat(xLimMin);
    const userXMax = parseFloat(xLimMax);
    let finalXMin, finalXMax;

    if (!isNaN(userXMin) && !isNaN(userXMax)) {
        //El usuario proporcionó valores válidos
        finalXMin = userXMin;
        finalXMax = userXMax;
    } else {
        // Si hay un error en los máximos y mínimos de x
        const xMinMax = d3.extent(data) as [number, number]; 
        finalXMin = xMinMax[0] - 10; 
        finalXMax = xMinMax[1] + 10;
    }
    
    const x = d3.scaleLinear().domain([finalXMin, finalXMax]).range([0, width2]);
    svg
      .append("g")
      .attr("transform", `translate(0, ${height2})`)
      .call(d3.axisBottom(x));

    // Obtener la desviación estándar de los datos
    const standardDeviation = d3.deviation(data) as number;

    // Usar un ancho de banda basado en la dispersión
    const bandwidth = standardDeviation * 0.15; 

    // Asegurar que el ancho de banda no sea demasiado pequeño
    const finalBandwidth = Math.max(bandwidth, 10);

    // Usa el ancho de banda dinámico
    // Compute kernel density estimation
    const kde = kernelDensityEstimator(kernelEpanechnikov(finalBandwidth), x.ticks(500));
    //kernelDensityEstimator siempre devuelve una matríz, pero typeScript no se fía de que sea así
    const density: [number, number][] = kde(data);//Así que se declara que density sea una matriz si o si

    //OBTENER LA DENSIDAD MÁXIMA de la segunda columna (la altura)
    // Usamos d3.max para encontrar el valor máximo de densidad (d[1]).
    const rawYMax = d3.max(density, (d) => d[1]);

    // Asigna 0 si es undefined
    const yMax = rawYMax === undefined || rawYMax === null ? 0 : rawYMax;
    
    // add the y Axis
    const y = d3.scaleLinear().range([height2, 0]).domain([0, yMax * 1.1]);
    svg.append("g").call(d3.axisLeft(y));
    //console.log(density)

    //Titulo del gráfico
    svg.append("text")
        .attr("x", width2 / 2)
        .attr("y", -margin.top / 2 + 20)
        .attr("text-anchor", "middle")
        .style("font-size", "16px")
        .text(title);


    // Plot the area
    svg
      .append("path")
      .attr("class", "mypath")
      .datum(density)
      .attr("fill", "#69b3a2")
      .attr("opacity", ".8")
      .attr("stroke", "#000")
      .attr("stroke-width", 1)
      .attr("stroke-linejoin", "round")
      .attr(
        "d",
        d3
          .line()
          .curve(d3.curveBasis)
          .x((d) => x(d[0]))
          .y((d) => y(d[1]))
      );
  }, [data, width, height]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: '300px' }}>
      <svg ref={ref} width={width} height={height} />
    </div>
  );
};

export default DensityPlot;

function kernelDensityEstimator(kernel: any, X: any) {
  return function (V: any) {
    return X.map(function (x: any) {
      return [
        x,
        d3.mean(V, function (v: any) {
          return kernel(x - v);
        }),
      ];
    });
  };
}
function kernelEpanechnikov(k: any) {
  return function (v: any) {
    return Math.abs((v /= k)) <= 1 ? (0.75 * (1 - v * v)) / k : 0;
  };
}
